import { useEffect, useMemo, useRef, useState } from "react";
import { Logo } from "../components/Logo";
import { Btn } from "../components/ui/Button";
import { Avatar, StorageBar } from "../components/ui/Common";
import { fileService, userService } from "../services/api";
import { formatBytes, formatDate, percent } from "../utils/format";
import { useResponsive } from "../hooks/useResponsive";

const defaultUploadState = {
  active: false,
  phase: "idle",
  progress: 0,
  fileName: "",
  loadedBytes: 0,
  totalBytes: 0,
  startedAt: 0,
};

const defaultApiAccess = {
  enabled: false,
  keyPrefix: "hv_live",
  last4: "",
  createdAt: null,
  rotatedAt: null,
  lastUsedAt: null,
  lastIp: "",
  lastMethod: "",
  lastEndpoint: "",
  baseUrl: "",
  usage: {
    totalRequests: 0,
    uploadRequests: 0,
    listRequests: 0,
    detailRequests: 0,
    downloadRequests: 0,
    bytesUploaded: 0,
    bytesDownloaded: 0,
  },
};

const defaultApiDocs = {
  baseUrl: "",
  docsUrl: "",
  authHeader: "X-API-Key",
  authScheme: "ApiKey",
  version: "v1",
  note: "",
  endpoints: [],
};

export function UserDashboard({ onNav, user, onLogout }) {
  const { isMobile, isTablet } = useResponsive();
  const [page, setPage] = useState("overview");
  const [files, setFiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(user);
  const [loading, setLoading] = useState(true);
  const [uploadState, setUploadState] = useState(defaultUploadState);
  const [apiAccess, setApiAccess] = useState(defaultApiAccess);
  const [apiLogs, setApiLogs] = useState([]);
  const [apiDocs, setApiDocs] = useState(defaultApiDocs);
  const [generatedApiKey, setGeneratedApiKey] = useState("");
  const [apiBusy, setApiBusy] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [actionState, setActionState] = useState({ type: "", fileId: "" });
  const fileInputRef = useRef(null);
  const dragDepthRef = useRef(0);
  const uploadActionRef = useRef(null);
  const statusTimeoutRef = useRef(null);
  const processingTimerRef = useRef(null);

  const safeStats = stats || {
    usedStorageBytes: 0,
    allocatedStorageBytes: 0,
    availableStorageBytes: 0,
  };
  const cardColumns = isMobile ? "1fr" : isTablet ? "repeat(2, minmax(0,1fr))" : "repeat(3, minmax(0,1fr))";
  const shellPadding = isMobile ? 16 : isTablet ? 20 : 28;

  const loadDashboard = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    setError("");
    try {
      const [profileResponse, statsResponse, filesResponse, apiResponse] = await Promise.allSettled([
        userService.getProfile(),
        userService.getStorageStats(),
        fileService.listFiles(),
        userService.getApiAccess(),
      ]);

      if (profileResponse.status !== "fulfilled" || statsResponse.status !== "fulfilled" || filesResponse.status !== "fulfilled") {
        throw (
          profileResponse.reason ||
          statsResponse.reason ||
          filesResponse.reason ||
          new Error("Failed to load your dashboard.")
        );
      }

      setProfile(profileResponse.value.data.user);
      setStats(statsResponse.value.data);
      setFiles(filesResponse.value.data.files || []);

      if (apiResponse.status === "fulfilled") {
        setApiAccess(apiResponse.value.data.apiAccess || defaultApiAccess);
        setApiLogs(apiResponse.value.data.recentLogs || []);
        setApiDocs(apiResponse.value.data.docs || defaultApiDocs);
      } else {
        setApiAccess(defaultApiAccess);
        setApiLogs([]);
        setApiDocs(defaultApiDocs);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load your dashboard.");
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const pushStatus = (tone, message) => {
    setStatus({ tone, message });
    window.clearTimeout(statusTimeoutRef.current);
    statusTimeoutRef.current = window.setTimeout(() => setStatus(null), 3200);
  };

  const uploadSelectedFile = async (file) => {
    if (!file) return;
    window.clearInterval(processingTimerRef.current);
    setUploadState({
      active: true,
      phase: "preparing",
      progress: 0,
      fileName: file.name,
      loadedBytes: 0,
      totalBytes: file.size || 0,
      startedAt: Date.now(),
    });
    setError("");
    try {
      await fileService.uploadFile(file, ({ loaded, total, percent: progressPercent }) => {
        window.clearInterval(processingTimerRef.current);
        setUploadState((current) => ({
          ...current,
          active: true,
          fileName: file.name,
          loadedBytes: loaded,
          totalBytes: total || file.size || 0,
          progress: Math.min(progressPercent, 100),
          phase: progressPercent >= 100 ? "processing" : "uploading",
        }));

        if (progressPercent >= 100) {
          processingTimerRef.current = window.setInterval(() => {
            setUploadState((current) => {
              if (!current.active || current.phase !== "processing" || current.progress >= 97) {
                window.clearInterval(processingTimerRef.current);
                return current;
              }
              return {
                ...current,
                progress: current.progress + 1,
              };
            });
          }, 220);
        }
      });
      window.clearInterval(processingTimerRef.current);
      setUploadState((current) => ({
        ...current,
        active: true,
        phase: "refreshing",
        progress: Math.max(current.progress, 98),
      }));
      await loadDashboard({ silent: true });
      setUploadState((current) => ({
        ...current,
        active: true,
        phase: "complete",
        progress: 100,
      }));
      pushStatus("success", `${file.name} uploaded successfully.`);
    } catch (err) {
      setError(err.response?.data?.message || "Upload failed.");
      window.clearInterval(processingTimerRef.current);
      setUploadState((current) => ({
        ...current,
        active: true,
        phase: "error",
      }));
    } finally {
      window.setTimeout(() => setUploadState(defaultUploadState), 1600);
    }
  };

  uploadActionRef.current = uploadSelectedFile;

  useEffect(() => {
    const hasFiles = (event) => Array.from(event.dataTransfer?.types || []).includes("Files");

    const handleDragEnter = (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current += 1;
      setDragActive(true);
    };

    const handleDragOver = (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      setDragActive(true);
    };

    const handleDragLeave = (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) {
        setDragActive(false);
      }
    };

    const handleDrop = async (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current = 0;
      setDragActive(false);
      const file = event.dataTransfer?.files?.[0];
      if (file && uploadActionRef.current) {
        await uploadActionRef.current(file);
      }
    };

    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.clearTimeout(statusTimeoutRef.current);
      window.clearInterval(processingTimerRef.current);
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
    };
  }, []);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    await uploadSelectedFile(file);
    event.target.value = "";
  };

  const askDelete = (file) => {
    setConfirmState({
      type: "delete",
      title: "Delete file?",
      message: `This will permanently remove "${file.name}" from your vault.`,
      confirmLabel: "Delete File",
      tone: "danger",
      file,
    });
  };

  const askPublicLink = (file) => {
    setConfirmState({
      type: "public-link",
      title: "Make this file public?",
      message: `After you create a public link for "${file.name}", it will stay public and cannot be reverted to private from this dashboard.`,
      confirmLabel: "Create Public Link",
      tone: "primary",
      file,
    });
  };

  const askApiKeyRotation = () => {
    setConfirmState({
      type: "api-key",
      title: apiAccess.enabled ? "Regenerate API key?" : "Create API key?",
      message: apiAccess.enabled
        ? "This will revoke your current API key immediately. Any app using the old key will stop working as soon as the new key is issued."
        : "Create a secure API key for this account. You will only be shown the full key once, so copy it to a safe backend or secret manager.",
      confirmLabel: apiAccess.enabled ? "Regenerate Key" : "Create API Key",
      tone: "primary",
    });
  };

  const runConfirmedAction = async () => {
    if (!confirmState?.type) return;

    const { type, file } = confirmState;

    if (type === "api-key") {
      setApiBusy("regenerate");
      try {
        const response = await userService.regenerateApiKey();
        setGeneratedApiKey(response.data.apiKey || "");
        setApiAccess(response.data.apiAccess || defaultApiAccess);
        setApiLogs(response.data.recentLogs || []);
        setApiDocs(response.data.docs || defaultApiDocs);
        pushStatus("success", apiAccess.enabled ? "API key regenerated. Copy it now." : "API key created. Copy it now.");
        setConfirmState(null);
      } catch (err) {
        setError(err.response?.data?.message || "Could not issue an API key.");
      } finally {
        setApiBusy("");
      }
      return;
    }

    if (!file) return;

    setActionState({ type, fileId: file.id });

    try {
      if (type === "delete") {
        await fileService.deleteFile(file.id);
        await loadDashboard({ silent: true });
        pushStatus("success", "File deleted.");
      }

      if (type === "public-link") {
          const response = await fileService.createPublicLink(file.id);
        await loadDashboard({ silent: true });
        if (response.data?.file?.share?.publicUrl) {
          await copyText(response.data.file.share.publicUrl);
        }
        pushStatus("success", "Public link created and copied.");
      }

      setConfirmState(null);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          (type === "delete" ? "Delete failed." : "Could not create public link.")
      );
    } finally {
      setActionState({ type: "", fileId: "" });
    }
  };

  const handleDownload = async (file) => {
    setActionState({ type: "download", fileId: file.id });
    try {
      const response = await fileService.downloadFile(file.id);
      const objectUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: file.mimeType || "application/octet-stream" })
      );
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = file.name || "download";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      pushStatus("success", `${file.name} downloaded.`);
    } catch (err) {
      setError(err.response?.data?.message || "Download failed.");
    } finally {
      setActionState({ type: "", fileId: "" });
    }
  };

  const handleCopy = async (value, message) => {
    try {
      await copyText(value);
      pushStatus("success", message);
    } catch (copyError) {
      setError("Copy failed.");
    }
  };

  const cards = useMemo(
    () => [
      {
        label: "Storage Used",
        value: formatBytes(safeStats.usedStorageBytes),
        sub: `${percent(safeStats.usedStorageBytes, safeStats.allocatedStorageBytes)}% of plan`,
        accent: "#60a5fa",
      },
      {
        label: "Available",
        value: formatBytes(safeStats.availableStorageBytes),
        sub: "remaining space",
        accent: "#34d399",
      },
      {
        label: "Files",
        value: String(files.length),
        sub: "stored in your vault",
        accent: "#f59e0b",
      },
    ],
    [files.length, safeStats.allocatedStorageBytes, safeStats.availableStorageBytes, safeStats.usedStorageBytes]
  );

  const recentFiles = files.slice(0, 5);
  const navItems = [
    { id: "overview", label: "Overview" },
    { id: "files", label: "Files" },
    { id: "api", label: "API" },
    { id: "settings", label: "Settings" },
  ];

  const titleMap = {
    overview: "Dashboard",
    files: "Files",
    api: "API Access",
    settings: "Settings",
  };

  const apiExamples = useMemo(() => {
    const baseUrl = apiDocs.baseUrl || apiAccess.baseUrl || "https://server.hexvault.in/api/v1";
    const apiKey = generatedApiKey || `${apiAccess.keyPrefix || "hv_live"}_replace_with_your_key`;
    return [
      {
        title: "List files",
        description: "Fetch the current user's vault contents from any secure backend or automation.",
        code: `curl -X GET "${baseUrl}/files" \\\n  -H "${apiDocs.authHeader || "X-API-Key"}: ${apiKey}"`,
      },
      {
        title: "Upload a file",
        description: "Send multipart uploads directly into the authenticated user's HexVault account.",
        code: `curl -X POST "${baseUrl}/files/upload" \\\n  -H "${apiDocs.authHeader || "X-API-Key"}: ${apiKey}" \\\n  -F "file=@/absolute/path/to/asset.jpg"`,
      },
      {
        title: "Inspect account",
        description: "Read current storage allocation, usage, and API status for the authenticated account.",
        code: `curl -X GET "${baseUrl}/me" \\\n  -H "${apiDocs.authHeader || "X-API-Key"}: ${apiKey}"`,
      },
    ];
  }, [apiAccess.baseUrl, apiAccess.keyPrefix, apiDocs.authHeader, apiDocs.baseUrl, generatedApiKey]);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#07111f",
        color: "#e2e8f0",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        fontFamily: "'Space Grotesk','Space Mono',system-ui",
      }}
    >
      <aside
        style={{
          width: isMobile ? "100%" : isTablet ? 220 : 250,
          background: "rgba(10,15,28,0.95)",
          borderRight: isMobile ? "none" : "1px solid rgba(255,255,255,0.06)",
          borderBottom: isMobile ? "1px solid rgba(255,255,255,0.06)" : "none",
          display: "flex",
          flexDirection: "column",
          padding: isMobile ? "16px 0 0" : "24px 0",
        }}
      >
        <div style={{ padding: "0 20px 24px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <Logo onClick={() => onNav("landing")} />
        </div>
        <div style={{ padding: "18px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <Avatar name={profile?.name || profile?.email || "HexVault"} size={42} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                title={profile?.name || "HexVault User"}
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {profile?.name || "HexVault User"}
              </div>
              <div
                title={profile?.email || ""}
                style={{
                  fontSize: 12,
                  color: "#64748b",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {profile?.email}
              </div>
            </div>
          </div>
          <StorageBar pct={percent(safeStats.usedStorageBytes, safeStats.allocatedStorageBytes || 1)} />
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 6 }}>
            {formatBytes(safeStats.usedStorageBytes)} of {formatBytes(safeStats.allocatedStorageBytes)}
          </div>
        </div>
        <nav style={{ flex: 1, padding: "0 10px", display: "flex", flexDirection: isMobile ? "row" : "column", gap: isMobile ? 8 : 0, overflowX: isMobile ? "auto" : "visible" }}>
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setPage(item.id)}
              style={{
                width: isMobile ? "auto" : "100%",
                display: "flex",
                alignItems: "center",
                padding: "10px 14px",
                marginBottom: 4,
                borderRadius: 12,
                border: "none",
                cursor: "pointer",
                background: page === item.id ? "rgba(59,130,246,0.14)" : "transparent",
                color: page === item.id ? "#93c5fd" : "#94a3b8",
                fontWeight: page === item.id ? 700 : 500,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <div style={{ padding: "12px 10px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <button
            type="button"
            onClick={onLogout}
            style={{
              width: "100%",
              padding: "10px 14px",
              borderRadius: 12,
              border: "none",
              background: "rgba(239,68,68,0.08)",
              color: "#fca5a5",
              cursor: "pointer",
              fontWeight: 600,
              textAlign: "left",
            }}
          >
            Sign Out
          </button>
        </div>
      </aside>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <header
          style={{
            minHeight: 72,
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            alignItems: isMobile ? "flex-start" : "center",
            justifyContent: "space-between",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 14 : 0,
            padding: isMobile ? "16px" : "0 28px",
            background: "rgba(7,17,31,0.85)",
            backdropFilter: "blur(16px)",
          }}
        >
          <div>
            <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.04em" }}>
              {titleMap[page] || "Dashboard"}
            </div>
            <div style={{ fontSize: 13, color: "#64748b" }}>
              Secure vault controls with live storage usage.
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, width: isMobile ? "100%" : "auto" }}>
            <input ref={fileInputRef} type="file" onChange={handleUpload} style={{ display: "none" }} />
            <Btn size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadState.active} style={{ width: isMobile ? "100%" : undefined, justifyContent: "center" }}>
              {uploadState.active ? "Uploading..." : "Upload File"}
            </Btn>
          </div>
        </header>

        <main style={{ flex: 1, padding: shellPadding, overflowY: "auto" }}>
          {status && <Banner message={status.message} tone={status.tone} />}
          {error && <Banner message={error} tone="error" />}
          {uploadState.active && <UploadActivity uploadState={uploadState} />}

          {loading ? (
            <DashboardSkeleton />
          ) : (
            <>
              {page === "overview" && (
                <div className="hexvault-fade-up">
                  <div style={{ display: "grid", gridTemplateColumns: cardColumns, gap: 16, marginBottom: 24 }}>
                    {cards.map((card) => (
                      <MetricCard key={card.label} card={card} />
                    ))}
                  </div>
                  <CapacityPanel stats={safeStats} />
                  <div style={panelStyle}>
                    <SectionHeader
                      title="Recent Files"
                      subtitle={`${files.length} total files in your vault`}
                    />
                    <FilesTable
                      files={recentFiles}
                      onAskDelete={askDelete}
                      onAskPublicLink={askPublicLink}
                      onDownload={handleDownload}
                      onCopy={handleCopy}
                      actionState={actionState}
                    />
                  </div>
                </div>
              )}

              {page === "files" && (
                <div className="hexvault-fade-up">
                  <div style={panelStyle}>
                    <SectionHeader
                      title="All Files"
                      subtitle="Manage downloads, public links, and file lifecycle from one place."
                    />
                    <FilesTable
                      files={files}
                      onAskDelete={askDelete}
                      onAskPublicLink={askPublicLink}
                      onDownload={handleDownload}
                      onCopy={handleCopy}
                      actionState={actionState}
                    />
                  </div>
                </div>
              )}

              {page === "api" && (
                <div className="hexvault-fade-up">
                  <ApiWorkspace
                    apiAccess={apiAccess}
                    apiLogs={apiLogs}
                    apiDocs={apiDocs}
                    apiExamples={apiExamples}
                    generatedApiKey={generatedApiKey}
                    apiBusy={apiBusy}
                    onCopy={handleCopy}
                    onRegenerate={askApiKeyRotation}
                  />
                </div>
              )}

              {page === "settings" && (
                <div className="hexvault-fade-up" style={panelStyle}>
                  <SectionHeader
                    title="Profile Settings"
                    subtitle="Keep your HexVault identity and account information up to date."
                  />
                  <ProfileForm user={profile} onSaved={loadDashboard} />
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {dragActive && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(7,17,31,0.58)",
            backdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 160,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              border: "1px dashed rgba(96,165,250,0.5)",
              background: "rgba(15,23,42,0.82)",
              borderRadius: 24,
              padding: isMobile ? "22px 20px" : "28px 34px",
              boxShadow: "0 30px 90px rgba(2,6,23,0.45)",
              textAlign: "center",
              width: isMobile ? "min(92vw, 420px)" : "auto",
            }}
          >
            <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-0.04em", color: "#f8fafc", marginBottom: 8 }}>
              Drop file to upload
            </div>
            <div style={{ color: "#93c5fd", fontSize: 14 }}>
              HexVault will start secure upload instantly.
            </div>
          </div>
        </div>
      )}

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          tone={confirmState.tone}
          busy={
            confirmState.type === "api-key"
              ? apiBusy === "regenerate"
              : actionState.type === confirmState.type &&
                actionState.fileId === (confirmState.file?.id || "")
          }
          onCancel={() => setConfirmState(null)}
          onConfirm={runConfirmedAction}
        />
      )}
    </div>
  );
}

function MetricCard({ card }) {
  return (
    <div style={panelStyle}>
      <div
        style={{
          fontSize: 12,
          color: "#64748b",
          fontWeight: 600,
          letterSpacing: "0.08em",
          marginBottom: 10,
        }}
      >
        {card.label.toUpperCase()}
      </div>
      <div style={{ fontSize: 30, fontWeight: 900, letterSpacing: "-0.04em", color: card.accent }}>
        {card.value}
      </div>
      <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>{card.sub}</div>
    </div>
  );
}

function UploadActivity({ uploadState }) {
  const { isMobile } = useResponsive();
  const tone =
    uploadState.phase === "error"
      ? "#f87171"
      : uploadState.phase === "complete"
        ? "#34d399"
        : "#60a5fa";

  const trackColor =
    uploadState.phase === "error"
      ? "linear-gradient(90deg, rgba(248,113,113,0.9), rgba(239,68,68,0.8))"
      : uploadState.phase === "complete"
        ? "linear-gradient(90deg, rgba(52,211,153,0.95), rgba(16,185,129,0.9))"
        : "linear-gradient(90deg, rgba(59,130,246,0.96), rgba(34,211,238,0.82))";

  const phaseCopy = {
    preparing: {
      title: "Preparing upload",
      subtitle: "Validating file and opening a secure transfer channel.",
    },
    uploading: {
      title: "Uploading in realtime",
      subtitle: "Sending bytes to HexVault now.",
    },
    processing: {
      title: "Processing file",
      subtitle: "Upload received. Calculating metadata and sealing storage records.",
    },
    refreshing: {
      title: "Updating dashboard",
      subtitle: "Refreshing your vault so the new file appears immediately.",
    },
    complete: {
      title: "Upload complete",
      subtitle: "Your file is now stored and ready for download or link creation.",
    },
    error: {
      title: "Upload interrupted",
      subtitle: "The transfer stopped before completion. You can retry any time.",
    },
  };

  const currentCopy = phaseCopy[uploadState.phase] || phaseCopy.preparing;

  return (
    <div
      className="hexvault-fade-up"
      style={{
        ...panelStyle,
        marginBottom: 18,
        padding: 18,
        background:
          uploadState.phase === "error"
            ? "linear-gradient(180deg, rgba(40,12,20,0.96), rgba(16,11,20,0.92))"
            : "linear-gradient(180deg, rgba(9,18,36,0.96), rgba(8,15,28,0.9))",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 18, alignItems: "flex-start", flexDirection: isMobile ? "column" : "row", marginBottom: 14 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                borderRadius: 999,
                padding: "6px 11px",
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.08)",
                color: tone,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {currentCopy.title}
            </span>
            <span style={{ color: "#cbd5e1", fontWeight: 700, fontSize: 13, minWidth: 0 }}>
              <span title={uploadState.fileName} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "inline-block", maxWidth: "100%", verticalAlign: "bottom" }}>
                {uploadState.fileName}
              </span>
            </span>
          </div>
          <div style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.7 }}>{currentCopy.subtitle}</div>
        </div>
        <div style={{ color: tone, fontSize: 28, fontWeight: 900, letterSpacing: "-0.05em", whiteSpace: "nowrap" }}>
          {uploadState.progress}%
        </div>
      </div>

      <div
        style={{
          height: 10,
          borderRadius: 999,
          background: "rgba(148,163,184,0.12)",
          overflow: "hidden",
          marginBottom: 14,
          boxShadow: "inset 0 1px 2px rgba(2,6,23,0.4)",
        }}
      >
        <div
          style={{
            width: `${Math.max(uploadState.progress, 4)}%`,
            height: "100%",
            borderRadius: 999,
            background: trackColor,
            boxShadow: `0 0 28px ${tone}55`,
            transition: "width 180ms ease",
          }}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 12 }}>
        <UploadMetaCard
          label="Transferred"
          value={`${formatBytes(uploadState.loadedBytes)} / ${formatBytes(uploadState.totalBytes)}`}
        />
        <UploadMetaCard
          label="Stage"
          value={currentCopy.title}
        />
        <UploadMetaCard
          label="Started"
          value={new Date(uploadState.startedAt || Date.now()).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}
        />
      </div>
    </div>
  );
}

function UploadMetaCard({ label, value }) {
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 16,
        padding: "12px 14px",
        minWidth: 0,
      }}
    >
      <div style={{ color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
        {label}
      </div>
      <div title={value} style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {value}
      </div>
    </div>
  );
}

function ApiWorkspace({
  apiAccess,
  apiLogs,
  apiDocs,
  apiExamples,
  generatedApiKey,
  apiBusy,
  onCopy,
  onRegenerate,
}) {
  const { isMobile, isTablet } = useResponsive();
  const usage = apiAccess.usage || defaultApiAccess.usage;
  const summaryCards = [
    {
      label: "Total API Calls",
      value: String(usage.totalRequests || 0),
      sub: "requests recorded on this account",
      accent: "#60a5fa",
    },
    {
      label: "Uploaded Via API",
      value: formatBytes(usage.bytesUploaded || 0),
      sub: `${usage.uploadRequests || 0} upload calls`,
      accent: "#34d399",
    },
    {
      label: "Downloaded Via API",
      value: formatBytes(usage.bytesDownloaded || 0),
      sub: `${usage.downloadRequests || 0} download calls`,
      accent: "#f59e0b",
    },
  ];

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2, minmax(0,1fr))" : "repeat(3, minmax(0,1fr))", gap: 16, marginBottom: 24 }}>
        {summaryCards.map((card) => (
          <MetricCard key={card.label} card={card} />
        ))}
      </div>

      <div style={{ ...panelStyle, marginBottom: 24 }}>
        <SectionHeader
          title="API Credentials"
          subtitle="Issue a private API key for backend integrations, automations, and external upload workflows."
        />
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.15fr 0.85fr", gap: 18 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
              <span
                style={{
                  ...statusBadgeStyle,
                  color: apiAccess.enabled ? "#34d399" : "#94a3b8",
                  borderColor: apiAccess.enabled ? "rgba(52,211,153,0.28)" : "rgba(148,163,184,0.18)",
                  background: apiAccess.enabled ? "rgba(52,211,153,0.1)" : "rgba(148,163,184,0.08)",
                }}
              >
                {apiAccess.enabled ? "Active key" : "No key yet"}
              </span>
              {apiAccess.enabled && (
                <span style={{ color: "#cbd5e1", fontSize: 13, fontWeight: 700 }}>
                  {(apiAccess.keyPrefix || "hv_live") + "_****" + (apiAccess.last4 || "")}
                </span>
              )}
            </div>

            <div style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.8, marginBottom: 16 }}>
              Use this key from secure server environments only. Regenerating a key revokes the previous one instantly, so old integrations stop working the moment the new key is created.
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
              <Btn onClick={onRegenerate} disabled={apiBusy === "regenerate"}>
                {apiBusy === "regenerate"
                  ? apiAccess.enabled
                    ? "Regenerating..."
                    : "Creating..."
                  : apiAccess.enabled
                    ? "Regenerate API Key"
                    : "Create API Key"}
              </Btn>
              <Btn
                variant="ghost"
                onClick={() => onCopy(apiDocs.baseUrl || apiAccess.baseUrl || "https://server.hexvault.in/api/v1", "API base URL copied.")}
              >
                Copy Base URL
              </Btn>
              {apiDocs.docsUrl && (
                <Btn
                  variant="ghost"
                  onClick={() => onCopy(apiDocs.docsUrl, "API docs URL copied.")}
                >
                  Copy Docs URL
                </Btn>
              )}
            </div>

            {generatedApiKey && (
              <div
                style={{
                  background: "linear-gradient(180deg, rgba(10,24,42,0.98), rgba(8,15,28,0.92))",
                  border: "1px solid rgba(96,165,250,0.22)",
                  borderRadius: 18,
                  padding: 16,
                  boxShadow: "0 22px 60px rgba(2,6,23,0.25)",
                }}
              >
                <div style={{ color: "#93c5fd", fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
                  Copy Now
                </div>
                <div style={{ color: "#e2e8f0", fontSize: 14, lineHeight: 1.7, marginBottom: 12 }}>
                  This is the only time the full API key will be shown. Store it in a secure backend environment variable or secret manager.
                </div>
                <div
                  title={generatedApiKey}
                  style={{
                    fontFamily: "'Space Mono', monospace",
                    color: "#f8fafc",
                    fontSize: 13,
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 14,
                    padding: "12px 14px",
                    marginBottom: 12,
                    wordBreak: "break-all",
                  }}
                >
                  {generatedApiKey}
                </div>
                <Btn variant="secondary" onClick={() => onCopy(generatedApiKey, "API key copied.")}>
                  Copy API Key
                </Btn>
              </div>
            )}
          </div>

          <div
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 18,
              padding: 16,
              minWidth: 0,
            }}
          >
            <div style={{ color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
              Endpoint Overview
            </div>
            <ApiMetaRow label="Base URL" value={apiDocs.baseUrl || apiAccess.baseUrl || "https://server.hexvault.in/api/v1"} />
            <ApiMetaRow label="Docs URL" value={apiDocs.docsUrl || "-"} />
            <ApiMetaRow label="Auth Header" value={apiDocs.authHeader || "X-API-Key"} />
            <ApiMetaRow label="Auth Scheme" value={apiDocs.authScheme || "ApiKey"} />
            <ApiMetaRow label="Version" value={apiDocs.version || "v1"} />
            <ApiMetaRow label="Last Used" value={formatDate(apiAccess.lastUsedAt)} />
            <ApiMetaRow label="Last Route" value={apiAccess.lastEndpoint || "-"} />
            <ApiMetaRow label="Last IP" value={apiAccess.lastIp || "-"} />
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.15fr 0.85fr", gap: 24 }}>
        <div style={panelStyle}>
          <SectionHeader
            title="API Docs"
            subtitle="Start with secure server-side requests. The old key stops working the moment you regenerate."
          />
          <div
            style={{
              marginBottom: 14,
              padding: "14px 16px",
              borderRadius: 16,
              border: "1px solid rgba(96,165,250,0.14)",
              background: "rgba(59,130,246,0.07)",
              color: "#bfdbfe",
              lineHeight: 1.7,
              fontSize: 13,
            }}
          >
            {apiDocs.note ||
              "Use the X-API-Key header from a secure backend or server-side environment. HexVault intentionally keeps API keys out of URLs so they do not leak into logs, browser history, or caches."}
          </div>
          {!!apiDocs.endpoints?.length && (
            <div
              style={{
                display: "grid",
                gap: 10,
                marginBottom: 14,
              }}
            >
              {apiDocs.endpoints.map((endpoint) => (
                <div
                  key={`${endpoint.method}-${endpoint.path}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "88px minmax(0, 1fr)",
                    gap: 12,
                    alignItems: "start",
                    padding: "12px 14px",
                    borderRadius: 16,
                    border: "1px solid rgba(255,255,255,0.06)",
                    background: "rgba(255,255,255,0.03)",
                  }}
                >
                  <span
                    style={{
                      ...statusBadgeStyle,
                      color: endpoint.method === "POST" ? "#34d399" : "#93c5fd",
                      background:
                        endpoint.method === "POST" ? "rgba(52,211,153,0.08)" : "rgba(59,130,246,0.08)",
                      borderColor:
                        endpoint.method === "POST" ? "rgba(52,211,153,0.18)" : "rgba(96,165,250,0.18)",
                      justifyContent: "center",
                    }}
                  >
                    {endpoint.method}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        color: "#f8fafc",
                        fontFamily: "'Space Mono', monospace",
                        fontSize: 12,
                        marginBottom: 4,
                        wordBreak: "break-word",
                      }}
                    >
                      {endpoint.path}
                    </div>
                    <div style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.6 }}>
                      {endpoint.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "grid", gap: 14 }}>
            {apiExamples.map((example) => (
              <CodeExampleCard key={example.title} example={example} onCopy={onCopy} />
            ))}
          </div>
        </div>

        <div style={panelStyle}>
          <SectionHeader
            title="Recent API Activity"
            subtitle="Track how this account has been using the programmable vault interface."
          />
          <ApiUsageList logs={apiLogs} />
        </div>
      </div>
    </>
  );
}

function ApiMetaRow({ label, value }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>
        {label}
      </div>
      <div title={value} style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 700, wordBreak: "break-word" }}>
        {value || "-"}
      </div>
    </div>
  );
}

function CodeExampleCard({ example, onCopy }) {
  const { isMobile } = useResponsive();
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 18,
        padding: 16,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "start", flexDirection: isMobile ? "column" : "row", marginBottom: 8 }}>
        <div>
          <div style={{ color: "#f8fafc", fontWeight: 800, fontSize: 15, marginBottom: 4 }}>{example.title}</div>
          <div style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.6 }}>{example.description}</div>
        </div>
        <Btn variant="ghost" size="sm" onClick={() => onCopy(example.code, `${example.title} example copied.`)}>
          Copy
        </Btn>
      </div>
      <pre
        style={{
          margin: 0,
          fontFamily: "'Space Mono', monospace",
          fontSize: 12,
          lineHeight: 1.8,
          color: "#cbd5e1",
          background: "rgba(2,6,23,0.5)",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 14,
          padding: "14px 16px",
          overflowX: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {example.code}
      </pre>
    </div>
  );
}

function ApiUsageList({ logs }) {
  if (!logs.length) {
    return (
      <EmptyState
        title="No API traffic yet"
        subtitle="Issue a key and start using HexVault from your backend to see request tracking here."
      />
    );
  }

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {logs.map((log) => (
        <div
          key={log.id}
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 16,
            padding: "14px 16px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ ...statusBadgeStyle, color: "#93c5fd", background: "rgba(59,130,246,0.1)", borderColor: "rgba(96,165,250,0.18)" }}>
                {log.method}
              </span>
              <span style={{ color: "#f8fafc", fontWeight: 700, fontSize: 13 }}>{log.endpoint}</span>
            </div>
            <span style={{ color: Number(log.statusCode || 0) < 400 ? "#34d399" : "#f87171", fontWeight: 700, fontSize: 12 }}>
              {log.statusCode || "-"}
            </span>
          </div>
          <div style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.7 }}>
            {log.fileName ? `${log.fileName} - ` : ""}
            {log.bytesUploaded ? `Uploaded ${formatBytes(log.bytesUploaded)} - ` : ""}
            {log.bytesDownloaded ? `Downloaded ${formatBytes(log.bytesDownloaded)} - ` : ""}
            {formatDate(log.createdAt)}
          </div>
        </div>
      ))}
    </div>
  );
}

function CapacityPanel({ stats }) {
  const { isMobile } = useResponsive();
  return (
    <div style={{ ...panelStyle, marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", flexDirection: isMobile ? "column" : "row", marginBottom: 12, gap: 14 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>Plan Capacity</div>
          <div style={{ color: "#64748b", fontSize: 13 }}>
            Monitor how much space is used before new uploads need more storage allocation.
          </div>
        </div>
        <div style={{ color: "#94a3b8", fontSize: 13, whiteSpace: "nowrap", alignSelf: "flex-start" }}>
          {formatBytes(stats.usedStorageBytes)} / {formatBytes(stats.allocatedStorageBytes)}
        </div>
      </div>
      <StorageBar pct={percent(stats.usedStorageBytes, stats.allocatedStorageBytes)} showLabel={false} />
    </div>
  );
}

function SectionHeader({ title, subtitle }) {
  const { isMobile } = useResponsive();
  return (
    <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "end", justifyContent: "space-between", flexDirection: isMobile ? "column" : "row", gap: 16, marginBottom: 16 }}>
      <div>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>{title}</div>
        <div style={{ color: "#64748b", fontSize: 13 }}>{subtitle}</div>
      </div>
    </div>
  );
}

function FilesTable({ files, onAskDelete, onAskPublicLink, onDownload, onCopy, actionState }) {
  if (!files.length) {
    return <EmptyState title="No files yet" subtitle="Upload your first file to start using HexVault." />;
  }

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <table style={{ width: "100%", minWidth: 840, borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ color: "#475569", fontSize: 11, letterSpacing: "0.08em" }}>
          {["Name", "Status", "Size", "Uploaded", "Asset ID", "Actions"].map((head) => (
            <th key={head} style={{ textAlign: "left", padding: "0 10px 12px", fontWeight: 600 }}>
              {head.toUpperCase()}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {files.map((file) => {
          const isCreatingLink = actionState.type === "public-link" && actionState.fileId === file.id;
          const isDeleting = actionState.type === "delete" && actionState.fileId === file.id;
          const isDownloading = actionState.type === "download" && actionState.fileId === file.id;

          return (
            <tr key={file.id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              <td style={{ padding: "16px 10px", color: "#f8fafc", fontWeight: 700, maxWidth: 320 }}>
                <div title={file.name} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {file.name}
                </div>
              </td>
              <td style={{ padding: "16px 10px" }}>
                <span
                  style={{
                    ...statusBadgeStyle,
                    color: file.share?.isPublic ? "#34d399" : "#94a3b8",
                    borderColor: file.share?.isPublic ? "rgba(52,211,153,0.28)" : "rgba(148,163,184,0.18)",
                    background: file.share?.isPublic ? "rgba(52,211,153,0.1)" : "rgba(148,163,184,0.08)",
                  }}
                >
                  {file.share?.isPublic ? "Public" : "Private"}
                </span>
              </td>
              <td style={{ padding: "16px 10px", color: "#94a3b8" }}>{formatBytes(file.sizeBytes)}</td>
              <td style={{ padding: "16px 10px", color: "#94a3b8", whiteSpace: "nowrap" }}>
                {formatDate(file.createdAt)}
              </td>
              <td style={{ padding: "16px 10px", maxWidth: 220 }}>
                <code
                  title={file.publicId || file.assetId || "-"}
                  style={{
                    color: "#60a5fa",
                    background: "rgba(59,130,246,0.1)",
                    padding: "5px 10px",
                    borderRadius: 10,
                    display: "inline-block",
                    maxWidth: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {file.publicId || file.assetId || "-"}
                </code>
              </td>
              <td style={{ padding: "16px 10px" }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {file.share?.publicUrl ? (
                    <>
                      <a href={file.share.publicUrl} target="_blank" rel="noreferrer" style={miniLinkStyle}>
                        Open Link
                      </a>
                      <button
                        type="button"
                        onClick={() => onCopy(file.share.publicUrl, "Public link copied.")}
                        style={miniButtonStyle}
                      >
                        Copy Link
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onAskPublicLink(file)}
                      style={miniButtonStyle}
                      disabled={isCreatingLink}
                    >
                      {isCreatingLink ? "Creating..." : "Create Link"}
                    </button>
                  )}
                  <button type="button" onClick={() => onDownload(file)} style={miniButtonStyle} disabled={isDownloading}>
                    {isDownloading ? "Preparing..." : "Download"}
                  </button>
                  <button
                    type="button"
                      onClick={() => onCopy(file.publicId || file.assetId || "", "Asset ID copied.")}
                      style={miniButtonStyle}
                    >
                      Copy Asset ID
                    </button>
                  <button
                    type="button"
                    onClick={() => onAskDelete(file)}
                    style={{ ...miniButtonStyle, color: "#fca5a5" }}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
      </table>
    </div>
  );
}

function ConfirmModal({ title, message, confirmLabel, tone, busy, onCancel, onConfirm }) {
  const { isMobile } = useResponsive();
  const accent = tone === "danger" ? "#ef4444" : "#3b82f6";
  const accentBg = tone === "danger" ? "rgba(239,68,68,0.1)" : "rgba(59,130,246,0.1)";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 180,
        background: "rgba(2,6,23,0.68)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "min(520px, 100%)",
          background: "rgba(8,15,28,0.94)",
          border: "1px solid rgba(148,163,184,0.14)",
          borderRadius: 24,
          boxShadow: "0 34px 90px rgba(2,6,23,0.42)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "22px 24px 16px" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 12px",
              borderRadius: 999,
              background: accentBg,
              color: accent,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              marginBottom: 16,
            }}
          >
            Confirmation
          </div>
          <div style={{ fontSize: 24, fontWeight: 900, letterSpacing: "-0.04em", marginBottom: 10 }}>
            {title}
          </div>
          <div style={{ color: "#94a3b8", lineHeight: 1.7, fontSize: 14 }}>{message}</div>
        </div>
        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.06)",
            padding: 18,
            display: "flex",
            flexDirection: isMobile ? "column-reverse" : "row",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          <Btn variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Btn>
          <Btn
            variant={tone === "danger" ? "danger" : "primary"}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Working..." : confirmLabel}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function copyText(value) {
  if (!value) return;
  return navigator.clipboard.writeText(value);
}

function ProfileForm({ user, onSaved }) {
  const { isMobile } = useResponsive();
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    try {
      await userService.updateProfile({ name });
      setMessage("Profile updated.");
      await onSaved({ silent: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: isMobile ? "100%" : 460 }}>
      {message && <Banner message={message} tone="success" />}
      <label
        style={{
          display: "block",
          fontSize: 12,
          color: "#94a3b8",
          marginBottom: 6,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        Display Name
      </label>
      <input value={name} onChange={(event) => setName(event.target.value)} style={inputStyle} />
      <label
        style={{
          display: "block",
          fontSize: 12,
          color: "#94a3b8",
          marginTop: 16,
          marginBottom: 6,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        Email
      </label>
      <input value={user?.email || ""} disabled style={{ ...inputStyle, opacity: 0.7 }} />
      <Btn onClick={handleSave} style={{ marginTop: 18 }} disabled={saving}>
        {saving ? "Saving..." : "Save Changes"}
      </Btn>
    </div>
  );
}

function Banner({ message, tone }) {
  const styles =
    tone === "error"
      ? {
          color: "#fca5a5",
          border: "rgba(239,68,68,0.3)",
          background: "rgba(239,68,68,0.1)",
        }
      : {
          color: "#86efac",
          border: "rgba(34,197,94,0.3)",
          background: "rgba(34,197,94,0.1)",
        };

  return (
    <div
      style={{
        background: styles.background,
        border: `1px solid ${styles.border}`,
        color: styles.color,
        borderRadius: 12,
        padding: "12px 14px",
        marginBottom: 16,
      }}
    >
      {message}
    </div>
  );
}

function EmptyState({ title, subtitle }) {
  return (
    <div style={{ textAlign: "center", padding: "56px 20px" }}>
      <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>{title}</div>
      <div style={{ color: "#64748b" }}>{subtitle}</div>
    </div>
  );
}

function DashboardSkeleton() {
  const { isMobile, isTablet } = useResponsive();
  return (
    <div className="hexvault-fade-up">
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2, minmax(0,1fr))" : "repeat(3, minmax(0,1fr))", gap: 16, marginBottom: 24 }}>
        {[1, 2, 3].map((item) => (
          <SkeletonCard key={item} height={166} />
        ))}
      </div>
      <SkeletonCard height={126} style={{ marginBottom: 24 }} />
      <SkeletonCard height={290} />
    </div>
  );
}

function SkeletonCard({ height, style = {} }) {
  return (
    <div
      style={{
        ...panelStyle,
        ...style,
        height,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(90deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.07) 48%, rgba(255,255,255,0.02) 100%)",
          transform: "translateX(-100%)",
          animation: "hexvaultSkeleton 1.5s ease-in-out infinite",
        }}
      />
    </div>
  );
}

const panelStyle = {
  background: "rgba(10,15,28,0.76)",
  border: "1px solid rgba(148,163,184,0.12)",
  borderRadius: 22,
  padding: 22,
  boxShadow: "0 24px 80px rgba(2,6,23,0.3)",
};

const inputStyle = {
  width: "100%",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 12,
  padding: "12px 14px",
  color: "#e2e8f0",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

const miniButtonStyle = {
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.08)",
  color: "#cbd5e1",
  borderRadius: 10,
  padding: "7px 12px",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 600,
};

const miniLinkStyle = {
  background: "rgba(59,130,246,0.12)",
  border: "1px solid rgba(96,165,250,0.2)",
  color: "#93c5fd",
  borderRadius: 10,
  padding: "7px 12px",
  fontSize: 12,
  textDecoration: "none",
  fontWeight: 600,
};

const statusBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  borderRadius: 999,
  padding: "6px 11px",
  border: "1px solid transparent",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};
